import type { VariantProps } from 'cva'
import { createContext, use, useId } from 'react'

import { cn, cva } from '~/utils/classnames'

const AvatarContext = createContext<{ size: 'sm' | 'md' | 'lg' }>({
  size: 'md',
})

const sizeStyle = cva({
  base: '',
  variants: {
    size: {
      sm: 'size-12 rounded-lg gradient-ring-rounded-lg',
      md: 'size-16 rounded-lg gradient-ring-rounded-lg',
      lg: 'size-20 rounded-xl gradient-ring-rounded-xl',
    },
  },
})

interface AvatarProps extends React.ComponentPropsWithRef<'div'> {
  size?: VariantProps<typeof sizeStyle>['size']
}

const AvatarRoot = ({
  className,
  size = 'md',
  children,
  ...props
}: AvatarProps) => {
  return (
    <AvatarContext value={{ size }}>
      <div
        className={cn(
          'bg-under/6 ring-foreground/12 relative flex aspect-square shrink-0 ring-1',
          sizeStyle({ size }),
          className
        )}
        {...props}
      >
        {children}
      </div>
    </AvatarContext>
  )
}

interface AvatarImageProps extends React.ComponentPropsWithRef<'img'> {
  src: string
}

const AvatarImage = ({ className, src, ...props }: AvatarImageProps) => {
  if (!src) return null

  return (
    <img
      className={cn(
        'absolute inset-0 z-2 size-full overflow-hidden rounded-[inherit] bg-black object-cover',
        className
      )}
      src={src ?? null}
      alt=""
      {...props}
      onError={(e) => {
        e.currentTarget.style.display = 'none'
      }}
    />
  )
}

const AvatarFallback = ({
  className,
  ...props
}: React.ComponentPropsWithRef<'svg'>) => {
  return (
    <AvatarGradient
      className={cn(
        'absolute inset-0 z-1 size-full overflow-hidden rounded-[inherit] object-cover',
        className
      )}
      {...props}
    />
  )
}

export const AvatarRing = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<'button'>) => {
  const { size } = use(AvatarContext)

  return (
    <button
      className={cn(
        'gradient-ring',
        'cursor-pointer transition-all hover:opacity-90 active:scale-[0.98]',
        sizeStyle({ size })
      )}
      {...props}
    >
      {children}
    </button>
  )
}

export const AvatarGradient = (props: React.ComponentPropsWithRef<'svg'>) => {
  const b = useId()
  const c = useId()
  const d = useId()
  const e = useId()
  const f = useId()

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 56 56"
      fill="none"
      {...props}
    >
      <g opacity=".64">
        <g filter={`url(#${b})`}>
          <path
            fill={`url(#${c})`}
            d="M19.688 22.75C9.188 22.75-7.875 1.02-7-6.563l67.632-3.062c6.708 6.125 16.1 22.75 0 40.25C40.507 52.5 32.812 22.75 19.688 22.75Z"
          />
        </g>
        <g filter={`url(#${d})`} opacity=".3">
          <path
            fill="currentColor"
            d="M-15.567 20.439c-10.428 0-11.297-17.086-10.428-25.628L22.67-9.625c6.663 6.9 15.99 25.628 0 45.343C2.683 60.36-2.531 20.439-15.567 20.439Z"
          />
          <path
            stroke="currentColor"
            strokeWidth=".875"
            d="M-15.567 20.439c-10.428 0-11.297-17.086-10.428-25.628L22.67-9.625c6.663 6.9 15.99 25.628 0 45.343C2.683 60.36-2.531 20.439-15.567 20.439Z"
          />
        </g>
        <g filter={`url(#${e})`} opacity=".3">
          <path
            fill="currentColor"
            d="M39.54 69.685c6.051 3.494.83 13.701-2.536 18.368L7.274 74.32C5.72 68.084 6.583 54.09 22.468 48.006c19.857-7.604 9.507 17.311 17.072 21.68Z"
          />
          <path
            stroke="currentColor"
            strokeWidth=".875"
            d="M39.54 69.685c6.051 3.494.83 13.701-2.536 18.368L7.274 74.32C5.72 68.084 6.583 54.09 22.468 48.006c19.857-7.604 9.507 17.311 17.072 21.68Z"
          />
        </g>
        <g filter={`url(#${f})`} opacity=".6">
          <path
            fill="currentColor"
            d="M42.643 41.529c-4.898 0-5.306-8.683-4.898-13.025l22.858-2.254c3.13 3.507 7.51 13.024 0 23.043-9.388 12.523-11.837-7.764-17.96-7.764Z"
          />
          <path
            stroke="currentColor"
            strokeWidth=".875"
            d="M42.643 41.529c-4.898 0-5.306-8.683-4.898-13.025l22.858-2.254c3.13 3.507 7.51 13.024 0 23.043-9.388 12.523-11.837-7.764-17.96-7.764Z"
          />
        </g>
      </g>
      <defs>
        <filter
          id={b}
          width="94.929"
          height="66.655"
          x="-16.22"
          y="-18.813"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur
            result="effect1_foregroundBlur_11_23"
            stdDeviation="4.594"
          />
        </filter>
        <filter
          id={d}
          width="81.725"
          height="77.367"
          x="-38.237"
          y="-21.63"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur
            result="effect1_foregroundBlur_11_23"
            stdDeviation="5.775"
          />
        </filter>
        <filter
          id={e}
          width="59.563"
          height="65.566"
          x="-5.256"
          y="34.585"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur
            result="effect1_foregroundBlur_11_23"
            stdDeviation="5.775"
          />
        </filter>
        <filter
          id={f}
          width="51.101"
          height="51.12"
          x="25.637"
          y="14.243"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur
            result="effect1_foregroundBlur_11_23"
            stdDeviation="5.775"
          />
        </filter>
        <linearGradient
          id={c}
          x1="38.063"
          x2="15.75"
          y1="14.875"
          y2="45.063"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="currentColor" />
          <stop offset="1" stopColor="currentColor" stopOpacity=".44" />
        </linearGradient>
      </defs>
    </svg>
  )
}

const Avatar = Object.assign(AvatarRoot, {
  Ring: AvatarRing,
  Image: AvatarImage,
  Fallback: AvatarFallback,
})

export { Avatar }
